// API client for PickleCourts backend
import type { VenueStatus } from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.picklecourts.rent';

// ============================================
// Types
// ============================================

interface ApiError {
  error: string;
}

export type AudienceLeaf = {
  field: string;
  operator: string;
  value?: unknown;
};

export type AudienceRelation =
  | 'membershipHistory'
  | 'membershipTransitions'
  | 'eventRegistrations'
  | 'attendance'
  | 'transactions';

export type AudienceRelationCondition = AudienceLeaf | {
  all: AudienceRelationCondition[];
} | {
  any: AudienceRelationCondition[];
} | {
  not: AudienceRelationCondition;
};

export type AudienceRelationNode = {
  relation: AudienceRelation;
  quantifier: 'exists' | 'not_exists' | 'count';
  where?: AudienceRelationCondition;
  operator?: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';
  value?: number;
};

export type AudienceMemberSelectionNode = {
  memberIds: string[];
  selectionSource?: {
    kind: 'spender_ranking';
    surface: 'transactions';
    completeFrom: string;
    completeThrough: string;
    surfaceRunId: string | null;
  };
};

export type AudienceFilterGroup = {
  version?: number;
  all: AudienceFilter[];
} | {
  version?: number;
  any: AudienceFilter[];
} | {
  version?: number;
  not: AudienceFilter;
};

export type AudienceFilter = AudienceLeaf | AudienceRelationNode | AudienceMemberSelectionNode | AudienceFilterGroup;

export interface AudienceAttribute {
  attributePath: string;
  displayName: string;
  dataType: 'string' | 'enum' | 'number' | 'money' | 'boolean' | 'date' | 'timestamp' | 'string_array' | 'enum_array';
  category?: string;
  allowedOperators: string[];
  options?: string[];
  coverageCount?: number;
  coveragePercent?: number;
  filterEnabled?: boolean;
  sortEnabled?: boolean;
  unit?: 'USD' | null;
  storageUnit?: 'cents' | null;
  definitionVersion?: number;
  sourceSemantics?: string;
  sensitivity?: 'standard' | 'sensitive' | string;
}

export interface AudienceRelationField {
  relation: AudienceRelation;
  field: string;
  displayName: string;
  dataType: string;
  unit?: 'USD' | null;
  storageUnit?: 'cents' | null;
  allowedOperators: string[];
  options: Array<string | number | boolean>;
  filterEnabled: boolean;
  sensitivity: string;
  sourceSemantics: string;
  coverageCount: number;
  coveragePercent: number;
  lastObservedAt?: string | null;
  schemaVersion: number;
  surface: string;
  completeFrom?: string | null;
  completeThrough?: string | null;
  lastSuccessfulAt?: string | null;
}

export interface AudienceCoverageHistoricalSurface {
  surface: string;
  label: string;
  available: boolean;
  relations: string[];
  coverageCount: number;
  completeFrom?: string | null;
  completeThrough?: string | null;
  lastSuccessfulAt?: string | null;
  executableRelations: Array<{ relation: string; fields: string[] }>;
  querySnippets: string[];
}

/**
 * Safe, presentation-ready coverage metadata. It describes availability but
 * never authorizes filters; `relations` remains the executable catalog.
 */
export interface AudienceCoverage {
  version: number;
  eligibleVisitorCount: number;
  scalarGroups: Array<{ category: string; attributePaths: string[]; querySnippets: string[] }>;
  historicalSurfaces: AudienceCoverageHistoricalSurface[];
  querySnippets: string[];
}

export interface AudienceStatus {
  state: 'not_entitled' | 'connection_required' | 'connection_invalid' | 'initial_sync_required' | 'sync_queued' | 'syncing' | 'ready' | 'stale' | 'sync_failed';
  reason?: { code: string; message: string } | null;
  serverNow: string;
  venue: { id: string; name: string; timezone: string; isPublished?: boolean; city?: string; regionState?: string };
  /** True when the venue's visitors are generated sample players (demo venue). */
  demo?: boolean;
  entitlement?: { featureKey: 'audiences'; status: 'trial' | 'active' | 'suspended' | 'canceled'; startsAt: string; endsAt?: string | null } | null;
  connection: { state: 'missing' | 'pending' | 'valid' | 'invalid'; integrationId?: string | null; credentialStatus: string; accountEmailMasked?: string | null; validatedAt?: string | null; lastErrorCode?: string | null; lastErrorMessage?: string | null; reportOrganizationApproval?: 'required' | 'verified' };
  data: { sourceId?: string | null; freshness: 'none' | 'fresh' | 'stale' | 'expired'; lastSuccessfulAt?: string | null; lastSuccessfulRunId?: string | null; sourceCutoffAt?: string | null; nextScheduledAt?: string | null; memberCount: number; attributeCount: number; consecutiveFailures: number };
  activeRun?: AudienceSyncRun | null;
  latestRun?: AudienceSyncRun | null;
  manualRefresh: { allowed: boolean; reasonCode?: string | null; availableAt?: string | null; cooldownSeconds: number };
  capabilities: Record<'connect' | 'refresh' | 'search' | 'save' | 'interpret' | 'export' | 'publish', AudienceCapability>;
  warnings?: string[];
}

export interface AudienceCapability { allowed: boolean; reasonCode?: string | null; message?: string | null }
export interface AudienceSyncRun { id: string; trigger: 'initial' | 'manual' | 'scheduled'; status: 'queued' | 'running' | 'succeeded' | 'failed' | 'dead_lettered'; attemptCount: number; scheduledFor?: string | null; startedAt?: string | null; completedAt?: string | null; progress?: { phase?: string; rowsSeen?: number; rowsInserted?: number; rowsUpdated?: number; rowsArchived?: number }; lastErrorCode?: string | null; lastErrorMessage?: string | null }

export interface AudienceVisitor {
  id: string;
  displayName: string;
  email?: string | null;
  recordStatus?: string;
  summary?: Record<string, string | number | boolean | null | undefined>;
}
export interface AudienceVisitorAttribute {
  attributePath: string;
  displayName: string;
  category: string;
  dataType: AudienceAttribute['dataType'];
  unit?: 'USD' | null;
  sensitivity?: string;
  value: unknown;
}
export interface AudienceVisitorProfile extends AudienceVisitor {
  phone?: string | null;
  profileAsOf?: string | null;
  attributes: AudienceVisitorAttribute[];
}
export interface AudienceVisitorActivity {
  activityKind: string;
  occurredAt: string;
  title: string;
  summary?: string | null;
  sourceEntity: string;
  sourceEntityId: string;
  amountCents?: number | null;
  metadata: Record<string, string | number | boolean | null>;
}
export interface AudienceSearchResult {
  members: AudienceVisitor[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  summary: { eligibleProfiles: number; matched: number; unknownCount?: number };
  filter: AudienceFilter;
  profileAsOf?: string | null;
  sourceUpdatedAt?: string | null;
}

export type AudienceInterpretationPhase =
  | 'queued'
  | 'reading_request'
  | 'checking_data'
  | 'building_criteria'
  | 'validating'
  | 'succeeded'
  | 'failed';

export interface AudienceInterpretationResult {
  action?: 'answer' | 'clarify' | 'audience' | 'save' | 'start_over';
  message?: string;
  audienceName?: string;
  filter: AudienceFilter;
  interpretation: string[];
  proof?: string[];
  warnings?: string[];
  requestId?: string | null;
  provider?: string | null;
  resultContext?: AudienceResultContext;
  matchedCount?: number;
  eligibleCount?: number;
  sourceUpdatedAt?: string | null;
}

export interface AudienceResultContext {
  type: 'spender_ranking';
  memberIds: string[];
  limit: number;
  dataFrom: string;
  dataThrough: string;
  sourceSurfaceRunId: string | null;
}

export interface AudienceInterpretationJob {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  phase: AudienceInterpretationPhase;
  progress?: {
    phase?: AudienceInterpretationPhase;
    heartbeatAt?: string | null;
    startedAt?: string | null;
  };
  result?: AudienceInterpretationResult | null;
  error?: { code: string; message: string; retryable: boolean } | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt: string;
}

export interface AudienceAgentFinalResult {
  type: 'answer' | 'clarification' | 'draft_update' | 'operation' | 'failure';
  displayText: string;
  draft?: {
    filter?: AudienceFilter;
    audienceName?: string;
    interpretation?: string[];
    warnings?: string[];
    [key: string]: unknown;
  } | null;
  continuation?: Record<string, unknown> | null;
  references?: Array<Record<string, unknown> | string>;
  grounding?: Record<string, unknown> | null;
}

export interface AudienceAgentRunEvent {
  id?: string;
  sequence: number;
  kind: 'status' | 'phase' | 'model' | 'tool' | 'clarification' | 'answer' | 'draft_update' | 'operation' | 'failure' | 'cancellation';
  status: 'started' | 'progress' | 'succeeded' | 'failed' | 'canceled';
  phase?: string | null;
  displayMessage?: string | null;
  skillId?: string | null;
  toolName?: string | null;
  toolCallId?: string | null;
  payload?: Record<string, unknown> | null;
  errorCode?: string | null;
  createdAt?: string;
}

export interface AudienceAgentRun {
  id: string;
  conversationId: string;
  turnNumber: number;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'canceled';
  phase: string;
  eventSequence: number;
  cancelRequested?: boolean;
  result?: AudienceAgentFinalResult | null;
  error?: { code: string; message: string; retryable: boolean } | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt: string;
}

export interface AudienceDataSource {
  id: string;
  displayName: string;
  sourceType?: string;
  state: string;
  mode?: string;
  memberCount?: number;
  attributeCount?: number;
  lastSuccessfulAt?: string | null;
  sourceCutoff?: string | null;
  lastRun?: { status?: string; insertedCount?: number; updatedCount?: number; message?: string } | null;
}

export interface AudienceBootstrap {
  status: AudienceStatus;
  attributes: AudienceAttribute[];
  relations?: AudienceRelationField[];
  coverage?: AudienceCoverage | null;
  dataSources: AudienceDataSource[];
  audiences: SavedAudience[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  preview?: {
    // `members` is the backend contract key; the portal presents these records as visitors.
    members: AudienceVisitor[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    summary: { eligibleProfiles: number; matched: number; unknownCount?: number };
    filter: AudienceFilter;
    profileAsOf?: string | null;
    sourceUpdatedAt?: string | null;
  } | null;
}

export interface SavedAudience {
  id: string;
  name: string;
  description?: string | null;
  sourcePrompt?: string | null;
  filterDefinition: AudienceFilter;
  lastEstimatedCount?: number | null;
  lastEvaluatedAt?: string | null;
  updatedAt?: string | null;
}

export interface AudiencePublication {
  id: string;
  audienceId?: string | null;
  destination: 'courtreserve';
  groupName: string;
  status: 'queued' | 'processing' | 'succeeded' | 'succeeded_with_warnings' | 'failed' | 'dead_lettered';
  attempts: number;
  matchedMemberCount: number;
  eligibleEmailCount: number;
  addedCount: number;
  unmatchedCount: number;
  failedCount: number;
  externalGroupId?: string | null;
  externalGroupUrl?: string | null;
  lastErrorCode?: string | null;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  sourceRunId?: string | null;
  destinationId?: string | null;
  removedCount?: number;
  intendedMemberCount?: number;
  warningCount?: number;
  canRetry?: boolean;
}

export interface LoginResponse {
  token: string;
  admin: {
    id: string;
    email: string;
    venueId: string;
    role: 'SUPERADMIN' | 'VENUE_ADMIN';
  };
}

export interface Venue {
  id: string;
  name: string;
  slug: string;
  timezone?: string;
  region?: {
    id: string;
    name: string;
    slug: string;
    state: string;
  } | null;
  config: {
    description?: string;
    email?: string;
    phone?: string;
    website?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
    photos?: Array<{ id: string; url: string; caption: string; isMain: boolean }>;
    operating_hours?: Array<{ dayOfWeek: number; open: string; close: string; isClosed: boolean }>;
    refund_policy?: {
      type: 'partial' | 'full' | 'none' | 'custom';
      fullRefundHours?: number;
      partialRefundHours?: number;
      partialRefundPercent?: number;
      customPolicy?: string;
    };
    waiver_policy?: { method: 'unknown' | 'at_venue' | 'checkout' | 'not_required'; evidence: string; instructions: string };
    waiver_pdf?: {
      enabled?: boolean;
      url?: string | null;
      file_name?: string | null;
      sha256?: string | null;
      uploaded_at?: string | null;
    };
    equipment_rental?: {
      enabled?: boolean;
      label?: string;
      price_cents?: number;
    };
    is_published?: boolean;
    booking_window_days?: number;
  };
  indoor_rate_cents: number;
  outdoor_rate_cents: number;
  payment_routing?: 'connect' | 'platform';
  created_at: string;
  updated_at: string;
}

export interface Court {
  id: string;
  venue_id: string;
  name: string;
  sport_type: string;
  court_type: 'indoor' | 'outdoor' | 'covered';
  mapped_external_name?: string | null;
  mapped_integration_type?: string | null;
  description?: string;
  amenities?: string[];
  photos?: Array<{ id: string; url: string; caption: string; isMain: boolean }>;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: string;
  court_id: string;
  customer_id: string;
  start_time: string;
  end_time: string;
  confirmation_code: string;
  status: 'checkout_pending' | 'pending_approval' | 'approved' | 'declined' | 'expired' | 'canceled' | 'completed'
    // legacy rows / cached responses:
    | 'manual_booking_approval' | 'pending' | 'confirmed';
  payment_flow?: string;
  source?: 'app' | 'manual' | 'phone' | 'walkin';
  notes?: string;
  created_at: string;
  updated_at: string;
  court_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  payment_amount_cents?: number;
  checkout_metadata?: {
    autoApproval?: {
      status?: string;
      courtreserveConfirmation?: string | null;
      externalRef?: { platform?: string; kind?: string; value?: string; verifiedAt?: string | null };
    };
    autoCancellation?: { status?: string };
    [key: string]: unknown;
  };
}

export interface Availability {
  id: string;
  court_id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string;
  end_time: string;
  hourly_rate_cents: number;
  is_available: boolean;
}

export interface PricingRule {
  id: string;
  name: string;
  courtTypeFilter: string;
  // Exactly one of daysOfWeek (recurring) / specificDate (one-off, self-expiring) is set.
  daysOfWeek: number[] | null;
  specificDate?: string | null;
  // Last date (inclusive) of a dated rule; equals specificDate for one day.
  endDate?: string | null;
  startTime: string;
  endTime: string;
  rateCents: number;
  priority: number;
  isActive: boolean;
}

export interface DashboardResponse {
  venue: Venue;
  courts: Court[];
  stats: {
    totalCourts: number;
    totalBookings: number;
    upcomingBookings: number;
  };
}

export interface CreateCourtRequest {
  name: string;
  sportType: string;
  courtType: 'indoor' | 'outdoor' | 'covered';
  description?: string;
  amenities?: string[];
}

export interface BulkAvailabilityRequest {
  courtIds: string[];
  dayOfWeek: number[];
  startTime: string;
  endTime: string;
  hourlyRateCents: number;
}

export type PromoFundedBy = 'venue' | 'platform';

export interface PromoWindow {
  id?: string;
  dowMask: number;
  localStartTime: string;
  localEndTime: string;
}

export interface SuperadminPromo {
  id: string;
  code: string;
  venueId: string | null;
  venueName: string | null;
  percentOff: number;
  maxRedemptionsTotal: number | null;
  maxRedemptionsPerCustomer: number;
  maxRedemptionsPerDay: number | null;
  maxDiscountCentsPerRedemption: number | null;
  maxDiscountCentsTotal: number | null;
  totalDiscountCentsUsed: number;
  fundedBy: PromoFundedBy;
  active: boolean;
  startsAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  redemptionCount: number;
  consumedCount: number;
  availableRedemptionCount: number;
  platformAbsorbedCents: number;
  pendingVenueReimbursementCents: number;
  createdAt: string;
  windows: PromoWindow[];
}

export interface PlatformPromoSettings {
  dailyPlatformBudgetCents: number | null;
  dailyPlatformSpentCents: number;
  spendWindowStartedAt: string;
  manuallyPaused: boolean;
  pausedReason: string | null;
  pausedAt: string | null;
}


// ============================================
// Revenue opportunities (court fill autopilot)
// ============================================

export interface RevenueOpportunityHour {
  hour: number;
  startUtc: string;
  endUtc: string;
  capacityCourtHours: number;
  busyCourtHours: number;
  openCourtHours: number;
  onBooksUtilization: number;
  baselineUtilization: number | null;
  projectedUtilization: number;
  projectedEmptyCourtHours: number;
  revenueAtRiskCents: number;
  averageHourlyRateCents: number;
}

/** One factor of a block's priority: a 0-1 value, its weight and its points. */
export interface RevenueScorePart {
  value: number;
  weight: number;
  points: number;
}

export interface RevenueScoreBreakdown {
  revenue: RevenueScorePart & { referenceCents: number };
  emptiness: RevenueScorePart;
  leadTime: RevenueScorePart & { idealDays: number; toleranceDays: number };
}

export interface RevenueOpportunity {
  date: string;
  startHourLocal: string;
  endHourLocal: string;
  startUtc: string;
  endUtc: string;
  leadTimeDays: number;
  capacityCourtHours: number;
  busyCourtHours: number;
  openCourtHours: number;
  projectedUtilization: number;
  projectedEmptyCourtHours: number;
  revenueAtRiskCents: number;
  currency: string | null;
  score: number;
  /** How the score was worked out; absent from older responses. */
  scoreBreakdown?: RevenueScoreBreakdown;
  hours: RevenueOpportunityHour[];
  /** How booked these hours usually are; null until there is enough history. */
  usualUtilization?: number | null;
  usualRevenueAtRiskCents?: number | null;
}

/** One horizon date: what is at risk, against how that weekday usually goes. */
export interface RevenueOpportunityDay {
  date: string;
  capacityCourtHours: number;
  projectedEmptyCourtHours: number;
  projectedUtilization: number | null;
  revenueAtRiskCents: number;
  usualUtilization: number | null;
  usualRevenueAtRiskCents: number | null;
}

export interface RevenueOpportunityResponse {
  venueId: string;
  timezone: string;
  currency: string | null;
  horizon: { startDate: string; endDate: string; days: number };
  thresholds: { maxUtilization: number; minEmptyCourtHours: number; baselineWeeks: number };
  courtCount: number;
  coverage: {
    hoursWithSupply: number;
    hasSellableInventory: boolean;
    occupancySource: 'provider_availability' | 'native_bookings';
    baselineModelled: boolean;
    baselineSampleDays?: number;
  };
  totals: {
    capacityCourtHours: number;
    busyCourtHours: number;
    projectedEmptyCourtHours: number;
    revenueAtRiskCents: number;
    promotableRevenueAtRiskCents: number;
    projectedUtilization: number;
    opportunityCount: number;
  };
  /** Absent from older API builds. */
  days?: RevenueOpportunityDay[];
  opportunities: RevenueOpportunity[];
  /** Sample data (the PickleCourts HQ demo venue), not real bookings. */
  demo?: boolean;
}

export interface RevenueOpportunityAudienceMember {
  memberId?: string;
  customerId?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email: string | null;
  score: number;
  reasons: string[];
}

export interface RevenueOpportunityAudience {
  population: 'provider_members' | 'native_customers';
  window: { localDate: string; startHour: number; endHour: number; leadTimeDays: number };
  candidateCount: number;
  returnedCount: number;
  deliverability: {
    withEmail: number;
    withPhone: number;
    marketingConsentTracked: boolean;
    suppressedUnsubscribed?: number;
  };
  audience: RevenueOpportunityAudienceMember[];
}

export interface RevenueOpportunityPublishResult {
  audience: { id: string; name: string; size: number; candidateCount: number };
  publication: { id: string; status: string } | null;
  created: boolean;
  delivery: { channel: string; sentByPickleCourts: boolean; reason: string };
  deliverability: RevenueOpportunityAudience['deliverability'];
}

export interface RevenueOpportunityEventCandidate {
  format: 'open_play' | 'round_robin' | 'clinic' | 'social';
  label: string;
  window: { startUtc: string; endUtc: string; hours: number };
  courts: number;
  capacity: number;
  capacityLimited: boolean;
  pricePerPlayerCents: number;
  ratingBand: { min: number | null; max: number | null } | null;
  expectedRegistrations: number;
  expectedRevenueCents: number;
  displacedCourtHours: number;
  discountCeilingCents: number;
  advantageCents: number;
  history: {
    basis: 'same_daypart' | 'any_time';
    eventCount: number;
    medianRegistrations: number;
    medianHours: number;
    medianFillRate: number | null;
  };
  reasons: string[];
}

export interface RevenueOpportunityEventPlan {
  venueId: string;
  supported: boolean;
  reason?: string;
  timezone?: string;
  percentOff?: number;
  recommendation?: {
    action: 'event' | 'discount';
    headline: string;
    event: RevenueOpportunityEventCandidate | null;
  };
  candidates?: RevenueOpportunityEventCandidate[];
  unavailable?: { format: string; reason: string }[];
  audience?: {
    likelyPlayers: number | null;
    coversExpectedRegistrations?: boolean;
    unavailableReason?: string;
  } | null;
}

export interface RevenueDemandCell {
  /** 0 = Sunday, in the venue's local time. */
  dayOfWeek: number;
  hour: number;
  devices: number;
}

export interface MarketOfferCampaignSummary {
  campaignId: string;
  localDate: string;
  /** 'shadow': nobody was contacted, these are would-have counts. */
  mode: 'shadow' | 'live';
  contacted: number;
  heldOut: number;
  suppressed: number;
  contactedBooked: number;
  heldOutBooked: number;
  contactedRate: number | null;
  heldOutRate: number | null;
}

export interface MarketOffersSummary {
  venueId: string;
  sinceDays: number;
  mode: 'off' | 'shadow';
  sending: false;
  campaigns: MarketOfferCampaignSummary[];
}

/**
 * What a weak-block player list led to. `came` counts list players who checked
 * in during the block; `holdout` is the equally-ranked players deliberately
 * not sent anything, when a campaign recorded them.
 */
export type RevenuePlayerListResult =
  | { status: 'not_published' | 'upcoming' | 'awaiting_data' }
  | {
    status: 'measured';
    players: number;
    came: number;
    holdout: { players: number; came: number } | null;
  };

/** A saved Audiences list as shown on the Revenue page. */
export interface RevenuePlayerList {
  id: string;
  name: string;
  description: string | null;
  sourcePrompt: string | null;
  size: number | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  origin: 'weak_block' | 'built';
  window: { localDate: string; startUtc: string; endUtc: string } | null;
  campaign: { id: string; channel: string; status: string } | null;
  result: RevenuePlayerListResult | null;
}

export interface RevenuePlayerListsResponse {
  venueId: string;
  demo?: boolean;
  lists: RevenuePlayerList[];
  totals: { lists: number; fromWeakBlocks: number; measured: number; players: number; came: number };
}

/** Money recovered by deal-code campaigns; provider-group campaigns are not measured in money. */
export interface RevenueRecoveredSummary {
  venueId: string;
  totals: {
    campaigns: number;
    bookingsRecovered: number;
    grossRecoveredCents: number;
    discountCostCents: number;
    netRecoveredCents: number;
    revenueAtRiskCents: number;
    recoveryRate: number | null;
    unmeasuredCampaigns: number;
    filledCourtHours: number;
  };
}

export interface RevenueDemandSignal {
  venueId: string;
  lookbackDays: number;
  minDevicesPerCell: number;
  timezone: string;
  // False until enough venues nearby have clicks for an area view not to be
  // just one venue's numbers; area.cells is then empty.
  areaAvailable: boolean;
  lookbackOptions: number[];
  // Device counts are rounded down to a multiple of this.
  deviceCountStep: number;
  area: { cells: RevenueDemandCell[]; suppressedCells: number };
  venue: { cells: RevenueDemandCell[]; suppressedCells: number };
}

/**
 * The venue's member opt-in link. `optedInCount` is null below
 * `minimumReported` (so a small count cannot single anyone out) and rounded
 * down to a multiple of five above it.
 */
export interface VenueMarketingInvite {
  url: string;
  optedInCount: number | null;
  minimumReported: number;
}

export type RevenueAutomationMode = 'off' | 'shadow' | 'act';

export interface RevenueAutomationRules {
  minScore: number;
  minRevenueAtRiskCents: number;
  minLeadHours: number;
  maxLeadDays: number;
  percentOff: number;
  audienceLimit: number;
  maxCampaignsPerWeek: number;
  maxDiscountCostCentsPerWeek: number;
  deferToEvents: boolean;
  listPublicly: boolean;
  syncedVenueDeals: boolean;
}

export interface RevenueAutomationSettings {
  venueId: string;
  mode: RevenueAutomationMode;
  rules: RevenueAutomationRules;
  updatedAt: string | null;
  // Whether the venue's paid Revenue Autopilot plan is on (GET only).
  autopilot?: { entitled: boolean };
}

export interface RevenueAutomationDecision {
  id: string;
  startUtc: string;
  endUtc: string;
  localDate: string;
  mode: 'shadow' | 'act';
  decision: 'launched' | 'would_launch' | 'skipped' | 'failed';
  reason: string;
  campaignId: string | null;
  opportunityScore: number | null;
  revenueAtRiskCents: number | null;
  maxDiscountCostCents: number;
  attempts: number;
  decidedAt: string;
}

export interface PromoRedemptionLedgerRow {
  redemptionId: string;
  consumedAt: string;
  bookingId: string | null;
  customerEmail: string | null;
  promoCodeId: string;
  code: string;
  percentOff: number;
  codeFundedBy: PromoFundedBy;
  redemptionFundedBy: PromoFundedBy | null;
  venueId: string | null;
  venueName: string | null;
  hasConnectedAccount: boolean;
  discountCents: number | null;
  originalSubtotalCents: number | null;
  platformAbsorbedCents: number;
  pendingVenueReimbursementCents: number;
  settledAt: string | null;
  settlementAmountCents: number | null;
  stripeTransferId: string | null;
}

export interface WaiverResolution {
  method: '' | 'at_venue' | 'signed_copy' | 'provider_complete' | 'not_required';
  evidence: string;
  instructions: string;
}

export interface ManualBookingApprovalRequest {
  waiver?: { signatureId?: string; typedName?: string; signedAt?: string; sha256?: string; accepted?: boolean } | null;
  waiverPolicy?: { method: string; evidence: string; instructions: string } | null;
  waiverResolution?: WaiverResolution | null;
  id: string;
  confirmationCode: string;
  status: 'pending_approval' | 'approved' | 'declined';
  venueId: string;
  venueName: string;
  venueTimezone?: string | null;
  venueSlug: string;
  courtId: string;
  courtName: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  startTime: string;
  endTime: string;
  paymentAmountCents: number;
  paymentStatus: string | null;
  paymentIntentId: string | null;
  totalFeeCents: number;
  serviceFeePercentCents: number;
  serviceFeeFlatCents: number;
  bookingFeeCents: number;
  subtotalAmountCents: number;
  // Mirrors backend MANUAL_QUEUE_STATUS wire values in backend/src/constants/checkout.js.
  queueStatus: 'pending' | 'claimed' | 'booked' | 'declined';
  claimedByAdminId: string | null;
  claimedAt: string | null;
  calledAt: string | null;
  venueConfirmationCode: string | null;
  internalBookingCostCents: number | null;
  actualVenueCostCents: number | null;
  capturedAmountCents: number | null;
  originalAuthAmountCents: number | null;
  releasedAmountCents: number | null;
  shortfallCents?: number | null;
  salesTaxAmountCents?: number;
  promoCode?: string | null;
  promoPercentOff?: number | null;
  promoDiscountCents?: number;
  // False in seller-collects tax markets, which still capture the full hold.
  exactCapture?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type QuoteRequestWorkflowStatus = 'pending_quote' | 'quoted' | 'declined' | 'expired' | 'converted';

/** A non-binding staff inquiry. It never represents a reservation or payment. */
export interface BookingQuoteRequest {
  id: string;
  venueId: string;
  venueName: string;
  venueTimezone?: string | null;
  courtId: string;
  courtName: string;
  customerId?: string | null;
  customerFirstName: string;
  customerLastName?: string | null;
  customerEmail: string;
  customerPhone?: string | null;
  requestedStartTime: string;
  requestedEndTime: string;
  currency: string;
  status: QuoteRequestWorkflowStatus;
  workflowStatus?: QuoteRequestWorkflowStatus;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
  quoteAmountCents?: number | null;
  quoteTaxCents?: number | null;
  quoteExpiresAt?: string | null;
}

// Mirrors backend planRequestToBookCapture (services/manual-booking-approval.js).
export interface ManualBookingCapturePreview {
  mode: 'exact' | 'full_quote';
  reason: string | null;
  authorizedCents: number;
  actualVenueCostCents: number;
  pickleCourtsFeeCents: number | null;
  promoDiscountCents: number;
  exactAmountCents: number | null;
  captureAmountCents: number;
  releasedCents: number;
  shortfallCents: number;
  platformFeeCents: number;
  venueCents: number;
}

export interface EarningsBooking {
  id: string;
  confirmation_code: string;
  start_time: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  court_name: string;
  amount_cents: number;
  platform_fee_amount: number | null;
  venue_amount: number | null;
  payment_status: string;
  payment_date: string;
  captured_at?: string | null;
  // Additive (backend ledger-truth fields; absent on older API builds)
  settlement_notes?: string | null;
  promo_credit?: boolean;
  // Itemized checkout pricing stamped at booking time (legacy rows: null)
  pricing_detail?: {
    baseAmountCents?: number;
    rentalAmountCents?: number;
    subtotalAmountCents?: number;
    serviceFeeAmountCents?: number;
    salesTaxBps?: number;
    salesTaxAmountCents?: number;
    totalAmountCents?: number;
  } | null;
  promo_detail?: {
    code?: string;
    percentOff?: number;
    discountCents?: number;
    originalAmountCents?: number;
    finalAmountCents?: number;
    fundedBy?: string;
  } | null;
}

export interface EarningsResponse {
  month: string;
  summary: {
    booking_count: number;
    gross_cents: number;
    platform_fee_cents: number;
    venue_amount_cents: number;
    refund_count?: number;
    refunded_cents?: number;
    sales_tax_cents?: number;
  };
  bookings: EarningsBooking[];
  venueNetCents?: number;
}

export interface VenueSettlementSummary {
  venueId: string;
  paymentRouting: 'connect' | 'platform';
  pending: {
    entryCount: number;
    grossCentsTotal: number;
    commissionCentsTotal: number;
    netToVenueCentsTotal: number;
    oldestEntryAt: string | null;
    newestEntryAt: string | null;
  };
  lastSettlement: { settlement_reference: string | null; settled_at: string | null } | null;
}

// ============================================
// Currency helpers
// ============================================

export function centsToDollars(cents: number): number {
  return cents / 100;
}

export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

export function audienceBuilderBasePath(role: string | null, selectedVenueId: string | null): string {
  if (role === 'SUPERADMIN' && selectedVenueId) {
    return `/api/admin/superadmin/venues/${encodeURIComponent(selectedVenueId)}/audience-builder`;
  }
  return '/api/admin/audience-builder';
}

// ============================================
// API Client
// ============================================

export class ApiClient {
  private token: string | null = null;
  private selectedVenueId: string | null = null;
  private requestGeneration = 0;
  private inFlightGets = new Map<string, Promise<unknown>>();
  private audienceStatusCache: { generation: number; storedAt: number; value: AudienceStatus } | null = null;

  constructor() {
    this.token = localStorage.getItem('okc_auth_token');
    this.selectedVenueId = localStorage.getItem('okc_selected_venue_id');
  }

  setToken(token: string | null) {
    this.token = token;
    this.requestGeneration += 1;
    this.inFlightGets.clear();
    this.audienceStatusCache = null;
    if (token) {
      localStorage.setItem('okc_auth_token', token);
    } else {
      localStorage.removeItem('okc_auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setSelectedVenueId(venueId: string | null) {
    this.selectedVenueId = venueId;
    this.requestGeneration += 1;
    this.inFlightGets.clear();
    this.audienceStatusCache = null;
    if (venueId) {
      localStorage.setItem('okc_selected_venue_id', venueId);
    } else {
      localStorage.removeItem('okc_selected_venue_id');
    }
  }

  getSelectedVenueId(): string | null {
    return this.selectedVenueId;
  }

  private getVenueQueryParam(): string {
    return this.selectedVenueId ? `?venueId=${this.selectedVenueId}` : '';
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const method = String(options.method || 'GET').toUpperCase();
    if (method !== 'GET') return this.performRequest<T>(endpoint, options);

    const key = `${this.requestGeneration}:${endpoint}`;
    const existing = this.inFlightGets.get(key);
    if (existing) return existing as Promise<T>;

    const pending = this.performRequest<T>(endpoint, options);
    this.inFlightGets.set(key, pending);
    try {
      return await pending;
    } finally {
      if (this.inFlightGets.get(key) === pending) this.inFlightGets.delete(key);
    }
  }

  private async performRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, { ...options, headers });
    const raw = await response.text();
    const data = raw ? JSON.parse(raw) : null;

    if (!response.ok) {
      if (response.status === 401 && this.token) {
        // Token expired or invalid — clear auth and redirect to login
        this.token = null;
        localStorage.removeItem('okc_auth_token');
        localStorage.removeItem('okc_business_user');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
      if (response.status === 304) {
        throw new Error('Request returned no fresh data');
      }
      throw new Error((data as ApiError | null)?.error || 'Request failed');
    }

    return (data ?? ({} as T)) as T;
  }

  // ============================================
  // Auth
  // ============================================

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.token);
    return response;
  }

  logout() {
    this.setToken(null);
    this.setSelectedVenueId(null);
  }

  async requestAdminPasswordReset(email: string): Promise<{ success: boolean }> {
    return this.request('/api/auth/admin/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetAdminPassword(token: string, newPassword: string): Promise<{ success: boolean }> {
    return this.request('/api/auth/admin/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // ============================================
  // Venue
  // ============================================

  async getVenue(): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue${this.getVenueQueryParam()}`);
  }

  async updateVenue(data: { name?: string; config?: Venue['config'] }): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateVenuePricing(data: { indoorRateCents?: number; outdoorRateCents?: number }): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/venue/pricing${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // Stripe Connect / Payments
  // ============================================

  async getStripeConnectStatus(): Promise<{
    venueId: string;
    connectedAccountId: string | null;
    onboardingStatus: string | null;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requirements: Record<string, any> | null;
    feeBps: number | null;
  }> {
    return this.request(`/api/stripe/connect/status${this.getVenueQueryParam()}`);
  }

  async createStripeConnectAccount(): Promise<{ connectedAccountId: string; onboardingStatus: string }> {
    return this.request(`/api/stripe/connect/account${this.getVenueQueryParam()}`, { method: 'POST' });
  }

  async getStripeOnboardingLink(returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
    return this.request(`/api/stripe/connect/onboarding-link${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ returnUrl, refreshUrl }),
    });
  }

  async getPaymentsEarnings(month?: string): Promise<EarningsResponse> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    if (month) params.set('month', month);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/payments/earnings${q}`);
  }

  // ============================================
  // Courts
  // ============================================

  async getCourts(): Promise<Court[]> {
    return this.request(`/api/admin/courts${this.getVenueQueryParam()}`);
  }

  async createCourt(data: CreateCourtRequest): Promise<Court> {
    return this.request(`/api/admin/courts${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCourt(id: string, data: Partial<CreateCourtRequest & { isActive?: boolean }>): Promise<Court> {
    return this.request(`/api/admin/courts/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCourt(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/courts/${id}${this.getVenueQueryParam()}`, { method: 'DELETE' });
  }

  // ============================================
  // Bookings
  // ============================================

  async getBookings(): Promise<Booking[]> {
    return this.request(`/api/admin/bookings${this.getVenueQueryParam()}`);
  }

  async updateBooking(id: string, data: {
    status?: Booking['status'];
    notes?: string;
    courtId?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    source?: string;
    customer?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  }): Promise<Booking> {
    return this.request(`/api/admin/bookings/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async approveReservation(id: string): Promise<{ reservation: { id: string; status: string }; payment: { status: string } }> {
    return this.request(`/api/reservations/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async declineReservation(id: string): Promise<{ reservation: { id: string; status: string }; payment: { status: string } }> {
    return this.request(`/api/reservations/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // ============================================
  // Availability
  // ============================================

  async getAvailabilitySummary(courtId: string): Promise<{ courtId: string; slots: Availability[] }> {
    return this.request(`/api/admin/courts/${courtId}/availability/summary${this.getVenueQueryParam()}`);
  }

  async createAvailability(
    courtId: string,
    data: {
      dayOfWeek?: number;
      specificDate?: string;
      startTime: string;
      endTime: string;
      hourlyRateCents: number;
      isAvailable?: boolean;
    }
  ): Promise<{ created: number; slots: Availability[] }> {
    return this.request(`/api/admin/courts/${courtId}/availability${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAvailability(
    courtId: string,
    options?: { dayOfWeek?: number; specificDate?: string; all?: boolean }
  ): Promise<{ deleted: number }> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    if (options?.all) params.set('all', 'true');
    if (options?.dayOfWeek !== undefined) params.set('dayOfWeek', String(options.dayOfWeek));
    if (options?.specificDate) params.set('specificDate', options.specificDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/courts/${courtId}/availability${query}`, { method: 'DELETE' });
  }

  async createBulkAvailability(data: BulkAvailabilityRequest): Promise<{ results: Array<{ courtId: string; created: number }> }> {
    return this.request(`/api/admin/courts/availability/bulk${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAvailabilitySlot(
    slotId: string,
    data: { hourlyRateCents?: number; isAvailable?: boolean }
  ): Promise<{ slot: Availability }> {
    return this.request(`/api/admin/availability/${slotId}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateAvailabilitySlots(
    slotIds: string[],
    data: { hourlyRateCents?: number; isAvailable?: boolean }
  ): Promise<{ updated: number }> {
    return this.request(`/api/admin/availability/bulk-update${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify({ slotIds, ...data }),
    });
  }

  async bulkCreateAvailabilitySlots(
    courtId: string,
    slots: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      hourlyRateCents: number;
      isAvailable: boolean;
    }>
  ): Promise<{ created: number }> {
    return this.request(`/api/admin/courts/${courtId}/availability/bulk-create${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ slots }),
    });
  }

  async getAvailabilityForDate(date: string): Promise<{
    date: string;
    slotsByCourtId: Record<string, Array<{
      start_time: string;
      end_time: string;
      hourly_rate_cents: number;
      status: 'available' | 'unavailable' | 'booked';
      isOverride?: boolean;
      booking?: {
        id: string;
        confirmationCode: string;
        customerName: string;
        status: string;
        notes?: string;
      };
    }>>;
    integration?: {
      integrationId: string;
      integrationType: string;
      lastSyncAt: string | null;
      syncStatus: string;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (this.selectedVenueId) params.set('venueId', this.selectedVenueId);
    params.set('date', date);
    return this.request(`/api/admin/availability/date?${params.toString()}`);
  }

  // ============================================
  // Dashboard
  // ============================================

  async getDashboard(): Promise<DashboardResponse> {
    return this.request('/api/admin/dashboard');
  }

  // ============================================
  // Photos
  // ============================================

  async getPhotoUploadUrl(
    filename: string,
    contentType: string,
    folder?: 'venue' | 'courts' | 'waivers'
  ): Promise<{ uploadUrl: string; publicUrl: string; key: string }> {
    return this.request('/api/admin/photos/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, folder }),
    });
  }

  async uploadPhotoToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo to S3');
    }
  }

  async deletePhoto(url: string): Promise<{ success: boolean }> {
    return this.request('/api/admin/photos', {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
  }

  // ============================================
  // Superadmin
  // ============================================

  async getSuperadminVenues(params?: { search?: string; page?: number; limit?: number; paymentRouting?: 'connect' | 'platform' }): Promise<{
    venues: Array<{
      id: string;
      name: string;
      slug: string;
      regionId: string | null;
      city: string;
      state: string;
      isPublished: boolean;
      venueMode: VenueStatus;
      paymentRouting: 'connect' | 'platform';
      mercuryRecipientId: string | null;
      availabilityStatusOverride: 'available' | 'unavailable' | 'unknown' | null;
      stripeStatus: 'pending' | 'restricted' | 'completed' | null;
      stripeChargesEnabled: boolean;
      stripePayoutsEnabled: boolean;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.paymentRouting) queryParams.set('paymentRouting', params.paymentRouting);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.request(`/api/admin/superadmin/venues${query}`);
  }

  // Full venue listing (id/name/location only) in one request. Use this for
  // dropdowns instead of paging getSuperadminVenues — sequential page loops
  // trip Cloudflare's per-IP rate limit now that the catalog is large.
  async getSuperadminVenueOptions(): Promise<{
    venues: Array<{ id: string; name: string; city: string; state: string }>;
  }> {
    return this.request('/api/admin/superadmin/venues/options');
  }

  async getManualBookingApprovalRequests(): Promise<{ requests: ManualBookingApprovalRequest[] }> {
    return this.request('/api/admin/superadmin/manual-booking-requests');
  }

  async claimManualBookingApprovalRequest(id: string): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/claim`, {
      method: 'POST',
    });
  }

  async getManualBookingSignedWaiver(id: string): Promise<{ url: string }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/signed-waiver`);
  }

  async completeManualBookingApprovalRequest(
    id: string,
    data: { actualVenueCostCents: number; venueConfirmationCode?: string; internalBookingCostCents?: number | null; waiverResolution: WaiverResolution }
  ): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async previewManualBookingCapture(
    id: string,
    data: { actualVenueCostCents: number }
  ): Promise<{ preview: ManualBookingCapturePreview }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/capture-preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async declineManualBookingApprovalRequest(
    id: string,
    data: { reason?: string }
  ): Promise<{ request: ManualBookingApprovalRequest }> {
    return this.request(`/api/admin/superadmin/manual-booking-requests/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBookingQuoteRequests(status: QuoteRequestWorkflowStatus = 'pending_quote'): Promise<{ requests: BookingQuoteRequest[] }> {
    const response = await this.request<{ requests: Record<string, unknown>[] }>(`/api/admin/superadmin/booking-quote-requests?status=${encodeURIComponent(status)}`);
    return {
      requests: (response.requests || []).map((row) => ({
        id: String(row.id), venueId: String(row.venue_id || row.venueId), venueName: String(row.venue_name || row.venueName || 'Unknown venue'), venueTimezone: (row.venue_timezone || row.venueTimezone || null) as string | null,
        courtId: String(row.court_id || row.courtId), courtName: String(row.court_name || row.courtName || 'Court'),
        customerId: (row.customer_id || row.customerId || null) as string | null,
        customerFirstName: String(row.customer_first_name || row.customerFirstName || ''), customerLastName: (row.customer_last_name || row.customerLastName || null) as string | null,
        customerEmail: String(row.customer_email || row.customerEmail || ''), customerPhone: (row.customer_phone || row.customerPhone || null) as string | null,
        requestedStartTime: String(row.requested_start_time || row.requestedStartTime), requestedEndTime: String(row.requested_end_time || row.requestedEndTime),
        currency: String(row.currency || 'USD'), status: String(row.status || 'pending_quote') as QuoteRequestWorkflowStatus,
        note: (row.staff_note || row.staffNote || (row.metadata && typeof row.metadata === 'object' ? (row.metadata as Record<string, unknown>).staffNote : row.note)) as string | null,
        quoteAmountCents: (row.quote_amount_cents || row.quoteAmountCents || null) as number | null,
        quoteTaxCents: (row.quote_tax_cents || row.quoteTaxCents || null) as number | null,
        quoteExpiresAt: (row.quote_expires_at || row.quoteExpiresAt || null) as string | null,
        createdAt: String(row.created_at || row.createdAt), updatedAt: String(row.updated_at || row.updatedAt),
      })),
    };
  }

  async updateBookingQuoteRequest(
    id: string,
    data: { status: QuoteRequestWorkflowStatus; staffNote?: string; quoteAmountCents?: number; quoteTaxCents?: number; quoteExpiresAt?: string }
  ): Promise<{ request: BookingQuoteRequest }> {
    return this.request(`/api/admin/superadmin/booking-quote-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getRegions(): Promise<Array<{ id: string; name: string; slug: string; state: string }>> {
    return this.request('/api/regions');
  }

  async setSuperadminVenueRegion(venueId: string, regionId: string): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        regionId,
      }),
    });
  }

  async setSuperadminVenueMode(venueId: string, venueMode: VenueStatus): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/mode`, {
      method: 'PUT',
      body: JSON.stringify({ venueMode }),
    });
  }

  async setSuperadminVenuePublished(venueId: string, published: boolean): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: {
          is_published: published,
        },
      }),
    });
  }

  async setSuperadminVenueAvailabilityOverride(
    venueId: string,
    value: 'available' | 'unavailable' | 'unknown' | null
  ): Promise<{ venue: Venue }> {
    const params = new URLSearchParams();
    params.set('venueId', venueId);

    return this.request(`/api/admin/venue?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: {
          map_availability_status_override: value,
        },
      }),
    });
  }

  async switchVenue(venueId: string): Promise<{ token: string; venue: { id: string; name: string; slug: string } }> {
    const response = await this.request<{ token: string; venue: { id: string; name: string; slug: string } }>(
      '/api/admin/superadmin/switch-venue',
      {
        method: 'POST',
        body: JSON.stringify({ venueId }),
      }
    );
    // Update the token
    this.setToken(response.token);
    return response;
  }

  // ============================================
  // Team/Admin Management
  // ============================================

  async getTeamMembers(): Promise<{ admins: Array<{
    id: string;
    email: string;
    role: string;
    created_at: string;
    invited_at?: string;
    accepted_at?: string;
    invited_by_email?: string;
  }> }> {
    return this.request(`/api/admin/team${this.getVenueQueryParam()}`);
  }

  async createTeamMember(data: {
    email: string;
    role?: 'VENUE_ADMIN' | 'VENUE_STAFF';
  }): Promise<{ admin: { id: string; email: string; role: string } }> {
    return this.request(`/api/admin/team${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeTeamMember(adminId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/team/${adminId}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Integrations / Sync
  // ============================================

  async getIntegrations(): Promise<{ integrations: Array<{
    id: string;
    venue_id: string;
    integration_type: string;
    external_id: string;
    config: any;
    status: string;
    sync_status: string;
    auto_sync_enabled: boolean;
    sync_interval_minutes: number;
    sync_active_start_hour: number | null;
    sync_active_end_hour: number | null;
    last_sync_at: string | null;
    last_error: string | null;
  }> }> {
    return this.request(`/api/integrations${this.getVenueQueryParam()}`);
  }

  async triggerSync(integrationId: string): Promise<{ queued: boolean; sync_status: string }> {
    return this.request(`/api/integrations/${integrationId}/sync${this.getVenueQueryParam()}`, {
      method: 'POST',
    });
  }

  async getSyncStatus(integrationId: string): Promise<{
    sync_status: string;
    last_sync_at: string | null;
    last_error: string | null;
  }> {
    return this.request(`/api/integrations/${integrationId}/sync-status${this.getVenueQueryParam()}`);
  }

  async updateAutoSync(integrationId: string, data: {
    enabled: boolean;
    intervalMinutes?: 5 | 15 | 30 | 60;
    activeStartHour?: number | null;
    activeEndHour?: number | null;
  }): Promise<{ integration: any }> {
    return this.request(`/api/integrations/${integrationId}/auto-sync${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteIntegration(integrationId: string): Promise<{ success: boolean }> {
    return this.request(`/api/integrations/${integrationId}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Pricing Rules
  // ============================================

  async getPricingRules(): Promise<{ rules: PricingRule[] }> {
    return this.request(`/api/admin/pricing-rules${this.getVenueQueryParam()}`);
  }

  async createPricingRule(data: Omit<PricingRule, 'id'>): Promise<{ rule: PricingRule }> {
    return this.request(`/api/admin/pricing-rules${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePricingRule(id: string, data: Partial<PricingRule>): Promise<{ rule: PricingRule }> {
    return this.request(`/api/admin/pricing-rules/${id}${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePricingRule(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/pricing-rules/${id}${this.getVenueQueryParam()}`, {
      method: 'DELETE',
    });
  }

  // Business Tiers & MSA
  // ============================================

  async getBusinessTiers(): Promise<{ tiers: Array<{ id: number; name: string; display_name: string; color: string; sort_order: number }> }> {
    return this.request('/api/admin/billing-tiers');
  }

  async setVenueTier(venueId: string, tierId: number | null): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tierId }),
    });
  }

  async setVenueCommission(venueId: string, commissionBps: number): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/commission`, {
      method: 'PUT',
      body: JSON.stringify({ commissionBps }),
    });
  }

  async setVenueContact(venueId: string, data: {
    legalName?: string;
    contactName?: string;
    contactTitle?: string;
    contactEmail?: string;
  }): Promise<{ success: boolean }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/contact`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async generateVenueDocs(venueId: string, opts?: { contactName?: string; contactEmail?: string; legalName?: string }): Promise<{ packet: { id: number; status: string; effectiveDate: string } }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/generate-docs`, {
      method: 'POST',
      body: opts ? JSON.stringify(opts) : undefined,
    });
  }

  async getMsaCurrent(): Promise<{
    packet: {
      id: number;
      status: string;
      commissionRateBps: number;
      effectiveDate: string;
      signedAt: string | null;
      signerName: string | null;
      docs: {
        masterAgreement: string;
        dataPrivacy: string;
        bookingOperations: string;
        marketingConsent: string;
        signaturePage: string;
      };
    } | null;
  }> {
    return this.request(`/api/admin/msa/current${this.getVenueQueryParam()}`);
  }

  async signMsa(packetId: number, signerName: string, signerEmail: string): Promise<{ success: boolean; signedAt: string }> {
    return this.request(`/api/admin/msa/sign${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify({ packetId, signerName, signerEmail }),
    });
  }

  // ============================================
  // Tenant admins remain scoped exclusively by their authenticated token.
  // Superadmins use the explicit support route for the venue selected in the
  // portal. The API still authorizes the role and venue on every request.
  // ============================================

  private audienceBuilderPath(path: string): string {
    let role: string | null = null;
    try {
      role = JSON.parse(localStorage.getItem('okc_business_user') || 'null')?.role || null;
    } catch {
      role = null;
    }

    return `${audienceBuilderBasePath(role, this.selectedVenueId)}${path}`;
  }

  private rememberAudienceStatus(value: AudienceStatus): AudienceStatus {
    this.audienceStatusCache = {
      generation: this.requestGeneration,
      storedAt: Date.now(),
      value,
    };
    return value;
  }

  // ============================================
  // Revenue opportunities
  // ============================================

  async getRevenueOpportunities(params: {
    horizonDays?: number;
    maxUtilization?: number;
    minEmptyCourtHours?: number;
  } = {}): Promise<RevenueOpportunityResponse> {
    const query = new URLSearchParams();
    if (this.selectedVenueId) query.set('venueId', this.selectedVenueId);
    if (params.horizonDays != null) query.set('horizonDays', String(params.horizonDays));
    if (params.maxUtilization != null) query.set('maxUtilization', String(params.maxUtilization));
    if (params.minEmptyCourtHours != null) {
      query.set('minEmptyCourtHours', String(params.minEmptyCourtHours));
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/admin/revenue-opportunities${suffix}`);
  }

  async previewRevenueOpportunityAudience(body: {
    startUtc: string;
    endUtc: string;
    limit?: number;
  }): Promise<RevenueOpportunityAudience> {
    return this.request(`/api/admin/revenue-opportunities/audience${this.getVenueQueryParam()}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** When players browsing this area look for courts (suppressed counts). */
  async getRevenueDemandSignal(days = 28): Promise<RevenueDemandSignal> {
    const venueParam = this.getVenueQueryParam();
    const joiner = venueParam ? '&' : '?';
    return this.request(`/api/admin/revenue-opportunities/demand${venueParam}${joiner}days=${days}`);
  }

  /** Nearby opted-in players each public campaign reached (or, in shadow, would have). */
  async getMarketOffersSummary(sinceDays = 30): Promise<MarketOffersSummary> {
    const venueParam = this.getVenueQueryParam();
    const joiner = venueParam ? '&' : '?';
    return this.request(`/api/admin/revenue-opportunities/market-offers${venueParam}${joiner}sinceDays=${sinceDays}`);
  }

  /** The link a venue sends its members to opt in to PickleCourts offers. */
  async getVenueMarketingInvite(): Promise<VenueMarketingInvite> {
    return this.request(`/api/admin/marketing/venue-invite${this.getVenueQueryParam()}`);
  }

  async getRevenueRecovered(sinceDays = 90): Promise<RevenueRecoveredSummary> {
    const venueParam = this.getVenueQueryParam();
    const joiner = venueParam ? '&' : '?';
    return this.request(`/api/admin/revenue-opportunities/recovered${venueParam}${joiner}sinceDays=${sinceDays}`);
  }

  async getRevenuePlayerLists(): Promise<RevenuePlayerListsResponse> {
    return this.request(`/api/admin/revenue-opportunities/player-lists${this.getVenueQueryParam()}`);
  }

  async getRevenueAutomation(): Promise<RevenueAutomationSettings> {
    return this.request(`/api/admin/revenue-opportunities/automation${this.getVenueQueryParam()}`);
  }

  async saveRevenueAutomation(body: {
    mode: RevenueAutomationMode;
    rules: Partial<RevenueAutomationRules>;
  }): Promise<RevenueAutomationSettings> {
    return this.request(`/api/admin/revenue-opportunities/automation${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /** PickleCourts staff only: turn the paid plan on or off for the venue. */
  async setRevenueAutopilot(enabled: boolean): Promise<{ entitled: boolean }> {
    return this.request(`/api/admin/revenue-opportunities/autopilot${this.getVenueQueryParam()}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async getRevenueAutomationDecisions(): Promise<{ decisions: RevenueAutomationDecision[] }> {
    return this.request(
      `/api/admin/revenue-opportunities/automation/decisions${this.getVenueQueryParam()}`
    );
  }

  async planRevenueOpportunityEvent(body: {
    startUtc: string;
    endUtc: string;
    percentOff?: number;
  }): Promise<RevenueOpportunityEventPlan> {
    return this.request(
      `/api/admin/revenue-opportunities/event/plan${this.getVenueQueryParam()}`,
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  async publishRevenueOpportunityAudience(body: {
    startUtc: string;
    endUtc: string;
    limit?: number;
  }): Promise<RevenueOpportunityPublishResult> {
    return this.request(
      `/api/admin/revenue-opportunities/audience/publish${this.getVenueQueryParam()}`,
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  async getAudienceBuilderStatus(options: { maxAgeMs?: number } = {}): Promise<AudienceStatus> {
    const maxAgeMs = Math.max(0, Number(options.maxAgeMs) || 0);
    const cached = this.audienceStatusCache;
    if (maxAgeMs > 0 && cached
      && cached.generation === this.requestGeneration
      && Date.now() - cached.storedAt < maxAgeMs) {
      return cached.value;
    }
    return this.rememberAudienceStatus(await this.request(this.audienceBuilderPath('/status')));
  }

  async getAudienceBootstrap(): Promise<AudienceBootstrap> {
    const result = await this.request<AudienceBootstrap>(this.audienceBuilderPath('/bootstrap'));
    this.rememberAudienceStatus(result.status);
    return result;
  }

  async saveAudienceConnection(data: { courtReserveUrl: string; adminEmail: string; adminPassword: string }, idempotencyKey?: string): Promise<{ connection: AudienceStatus['connection']; run?: AudienceSyncRun; reused?: boolean }> {
    return this.request(this.audienceBuilderPath('/connection'), { method: 'PUT', headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined, body: JSON.stringify(data) });
  }

  async validateAudienceConnection(): Promise<{ connection: AudienceStatus['connection'] }> {
    return this.request(this.audienceBuilderPath('/connection/validate'), { method: 'POST', body: '{}' });
  }

  async revokeAudienceConnection(): Promise<void> {
    await this.request(this.audienceBuilderPath('/connection'), { method: 'DELETE' });
  }

  async requestAudienceSync(idempotencyKey?: string): Promise<{ run: AudienceSyncRun; reused: boolean }> {
    return this.request(this.audienceBuilderPath('/sync-runs'), { method: 'POST', headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined, body: JSON.stringify({ trigger: 'manual' }) });
  }

  async getAudienceSyncRuns(limit = 20): Promise<{ runs: AudienceSyncRun[]; nextCursor?: string | null }> {
    return this.request(`${this.audienceBuilderPath('/sync-runs')}?limit=${limit}`);
  }

  async getAudienceAttributes(): Promise<{ attributes: AudienceAttribute[] }> {
    return this.request(this.audienceBuilderPath('/attributes'));
  }

  async getAudienceDataSources(): Promise<{ dataSources: AudienceDataSource[] }> {
    return this.request(this.audienceBuilderPath('/data-sources'));
  }

  async searchAudienceVisitors(data: { query?: string; filter: AudienceFilter; page?: number; limit?: number }, options: { signal?: AbortSignal } = {}): Promise<AudienceSearchResult> {
    return this.request(this.audienceBuilderPath('/members/search'), {
      method: 'POST', body: JSON.stringify(data), signal: options.signal,
    });
  }

  async getAudienceVisitor(memberId: string): Promise<{ member: AudienceVisitorProfile }> {
    return this.request(this.audienceBuilderPath(`/members/${memberId}`));
  }

  async getAudienceVisitorActivity(memberId: string, limit = 100): Promise<{
    activities: AudienceVisitorActivity[];
    hasMore: boolean;
    nextBefore?: string | null;
  }> {
    return this.request(this.audienceBuilderPath(`/members/${memberId}/activity?limit=${Math.min(100, Math.max(1, limit))}`));
  }

  async getAudienceEmails(data: { query?: string; filter: AudienceFilter }): Promise<{ emails: string[]; count: number; truncated?: boolean }> {
    return this.request(this.audienceBuilderPath('/members/emails'), {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async reportAudienceIssue(data: {
    issue?: string;
    context?: {
      page?: string;
      clientTimestamp?: string;
      conversationId?: string;
      conversationTitle?: string;
      audienceId?: string;
      audienceName?: string;
      lastPrompt?: string;
    };
  }): Promise<{ reported: boolean }> {
    return this.request(this.audienceBuilderPath('/issues'), {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async getSavedAudiences(page = 1, limit = 20): Promise<{ audiences: SavedAudience[]; pagination?: { page: number; totalPages: number; total: number } }> {
    const base = this.audienceBuilderPath('/audiences');
    return this.request(`${base}${base.includes('?') ? '&' : '?'}page=${page}&limit=${limit}`);
  }

  async saveAudience(data: { name: string; description?: string; sourcePrompt?: string | null; filter: AudienceFilter }): Promise<{ audience: SavedAudience }> {
    return this.request(this.audienceBuilderPath('/audiences'), { method: 'POST', body: JSON.stringify(data) });
  }

  async updateSavedAudience(id: string, data: { name: string; description?: string; sourcePrompt?: string | null; filter: AudienceFilter; updatedAt?: string | null }): Promise<{ audience: SavedAudience }> {
    return this.request(this.audienceBuilderPath(`/audiences/${id}`), { method: 'PUT', headers: data.updatedAt ? { 'If-Match': `"${data.updatedAt}"` } : undefined, body: JSON.stringify(data) });
  }

  async previewSavedAudience(id: string, options: { page?: number; limit?: number } = {}): Promise<AudienceSearchResult & { audience?: SavedAudience }> {
    return this.request(this.audienceBuilderPath(`/audiences/${id}/preview`), { method: 'POST', body: JSON.stringify(options) });
  }

  async deleteSavedAudience(id: string): Promise<void> {
    await this.request(this.audienceBuilderPath(`/audiences/${id}`), { method: 'DELETE' });
  }

  async interpretAudienceFilter(text: string, currentFilter: AudienceFilter): Promise<{
    filter: AudienceFilter;
    interpretation: string[];
    warnings?: string[];
    provider?: string;
  }> {
    return this.request(this.audienceBuilderPath('/filters/interpret'), {
      method: 'POST', body: JSON.stringify({ text, currentFilter }),
    });
  }

  async startAudienceInterpretationJob(
    text: string,
    currentFilter: AudienceFilter,
    idempotencyKey: string,
    conversation?: Array<{ role: 'user' | 'assistant'; text: string; resultContext?: AudienceResultContext }>,
    audienceContext?: { name: string; savedAudienceId?: string | null; hasActiveDraft: boolean } | null,
  ): Promise<{ job: AudienceInterpretationJob; reused: boolean }> {
    return this.request(this.audienceBuilderPath('/filters/interpret/jobs'), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ text, currentFilter, conversation, audienceContext }),
    });
  }

  async getAudienceInterpretationJob(jobId: string): Promise<{ job: AudienceInterpretationJob }> {
    return this.request(this.audienceBuilderPath(`/filters/interpret/jobs/${jobId}`));
  }

  async startAudienceAgentRun(data: {
    text: string;
    conversationId: string;
    currentFilter: AudienceFilter;
    conversation?: Array<{ role: 'user' | 'assistant'; text: string; resultContext?: AudienceResultContext }>;
    audienceContext?: { name: string; savedAudienceId?: string | null; hasActiveDraft: boolean } | null;
    idempotencyKey: string;
  }): Promise<{ run: AudienceAgentRun; reused: boolean; acknowledgement?: string }> {
    return this.request(this.audienceBuilderPath('/agent/runs'), {
      method: 'POST',
      headers: { 'Idempotency-Key': data.idempotencyKey },
      body: JSON.stringify({
        text: data.text,
        conversationId: data.conversationId,
        currentFilter: data.currentFilter,
        conversation: data.conversation,
        audienceContext: data.audienceContext,
      }),
    });
  }

  async getAudienceAgentRun(runId: string): Promise<{ run: AudienceAgentRun }> {
    return this.request(this.audienceBuilderPath(`/agent/runs/${encodeURIComponent(runId)}`));
  }

  async cancelAudienceAgentRun(runId: string): Promise<{ run: AudienceAgentRun }> {
    return this.request(this.audienceBuilderPath(`/agent/runs/${encodeURIComponent(runId)}/cancel`), {
      method: 'POST', body: '{}',
    });
  }

  async streamAudienceAgentRunEvents(
    runId: string,
    options: {
      afterSequence?: number;
      signal?: AbortSignal;
      onEvent: (event: AudienceAgentRunEvent) => void;
    },
  ): Promise<number> {
    const afterSequence = Math.max(0, Number(options.afterSequence) || 0);
    const path = this.audienceBuilderPath(`/agent/runs/${encodeURIComponent(runId)}/events`);
    const url = `${API_BASE_URL}${path}${path.includes('?') ? '&' : '?'}afterSequence=${afterSequence}`;
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const response = await fetch(url, { headers, signal: options.signal });
    if (!response.ok || !response.body) throw new Error('Agent event stream is unavailable');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastSequence = afterSequence;
    const emitBlock = (block: string) => {
      let id: number | null = null;
      const dataLines: string[] = [];
      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith('id:')) id = Number.parseInt(line.slice(3).trim(), 10);
        if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
      }
      if (!dataLines.length) return;
      const parsed = JSON.parse(dataLines.join('\n')) as AudienceAgentRunEvent | { event: AudienceAgentRunEvent };
      const event = 'event' in parsed ? parsed.event : parsed;
      const sequence = Number(event.sequence || id || 0);
      if (!Number.isInteger(sequence) || sequence <= lastSequence) return;
      lastSequence = sequence;
      options.onEvent({ ...event, sequence });
    };
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() || '';
      for (const block of blocks) emitBlock(block);
      if (done) break;
    }
    if (buffer.trim()) emitBlock(buffer);
    return lastSequence;
  }

  async publishAudienceToCourtReserve(audienceId: string, data: { groupName?: string; idempotencyKey: string }): Promise<{ publication: AudiencePublication }> {
    return this.request(this.audienceBuilderPath(`/audiences/${audienceId}/publications`), {
      method: 'POST', headers: { 'Idempotency-Key': data.idempotencyKey }, body: JSON.stringify({ destination: 'courtreserve', groupName: data.groupName }),
    });
  }

  async getAudiencePublication(id: string): Promise<{ publication: AudiencePublication }> {
    return this.request(this.audienceBuilderPath(`/publications/${id}`));
  }

  async getAudiencePublications(audienceId: string): Promise<{ publications: AudiencePublication[]; nextCursor?: string | null }> {
    return this.request(this.audienceBuilderPath(`/audiences/${audienceId}/publications`));
  }

  async retryAudiencePublication(publicationId: string, idempotencyKey: string): Promise<{ publication: AudiencePublication; reused?: boolean }> {
    return this.request(this.audienceBuilderPath(`/publications/${publicationId}/retry`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: '{}' });
  }

  // ============================================
  // Health
  // ============================================

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  async getSuperadminPromos(venueId?: string | null): Promise<{ promos: SuperadminPromo[] }> {
    const query = venueId ? `?venueId=${encodeURIComponent(venueId)}` : '';
    return this.request(`/api/admin/superadmin/promos${query}`);
  }

  async createSuperadminPromo(data: {
    code?: string;
    venueId?: string | null;
    percentOff: number;
    fundedBy?: PromoFundedBy;
    maxDiscountCentsPerRedemption?: number | null;
    maxDiscountCentsTotal?: number | null;
    maxRedemptionsTotal?: number | null;
    maxRedemptionsPerCustomer?: number;
    maxRedemptionsPerDay?: number | null;
    startsAt?: string | null;
    expiresAt?: string | null;
    notes?: string | null;
    windows?: PromoWindow[];
  }): Promise<{ promo: Partial<SuperadminPromo> }> {
    return this.request('/api/admin/superadmin/promos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSuperadminPromoSettings(): Promise<{ settings: PlatformPromoSettings }> {
    return this.request('/api/admin/superadmin/promos/settings');
  }

  async updateSuperadminPromoSettings(data: {
    dailyPlatformBudgetCents?: number | null;
    manuallyPaused?: boolean;
    pausedReason?: string | null;
  }): Promise<{ settings: PlatformPromoSettings }> {
    return this.request('/api/admin/superadmin/promos/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async listSuperadminPromoRedemptions(opts: { onlyOutstanding?: boolean } = {}): Promise<{ redemptions: PromoRedemptionLedgerRow[] }> {
    const q = opts.onlyOutstanding ? '?onlyOutstanding=true' : '';
    return this.request(`/api/admin/superadmin/promos/redemptions${q}`);
  }

  async settleSuperadminPromoRedemption(redemptionId: string): Promise<{
    alreadySettled: boolean;
    transferId: string | null;
    redemption: { id: string; settledAt: string | null; settlementAmountCents: number | null; stripeTransferId: string | null };
  }> {
    return this.request(`/api/admin/superadmin/promos/redemptions/${redemptionId}/settle`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async setSuperadminPromoActive(promoId: string, active: boolean): Promise<{ promo: { id: string; active: boolean } }> {
    return this.request(`/api/admin/superadmin/promos/${promoId}`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
  }

  async updateSuperadminPromoWindows(promoId: string, windows: PromoWindow[]): Promise<{ promo: { id: string; windows: PromoWindow[] } }> {
    return this.request(`/api/admin/superadmin/promos/${promoId}`, {
      method: 'PUT',
      body: JSON.stringify({ windows }),
    });
  }

  // --- Settlement (platform-collect partner monthly ledger) ---

  async getVenueSettlementSummary(venueId: string): Promise<VenueSettlementSummary> {
    return this.request(`/api/admin/venues/${venueId}/settlement-summary`);
  }

  async listSettlementEntries(
    venueId: string,
    opts: { status?: 'pending' | 'settled' | 'voided'; limit?: number } = {}
  ): Promise<{ entries: Array<SettlementEntry> }> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.limit) params.set('limit', String(opts.limit));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries${q}`);
  }

  async markSettlementEntriesSettled(
    venueId: string,
    data: { entryIds: string[]; reference: string; notes?: string }
  ): Promise<{ updated: SettlementEntry[]; skipped?: string[]; message?: string }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries/mark-settled`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async voidSettlementEntry(entryId: string, notes: string): Promise<{ entry: SettlementEntry }> {
    return this.request(`/api/admin/superadmin/settlement-entries/${entryId}/void`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async recordSettlementManualAdjustment(
    venueId: string,
    data: { paymentId: string; bookingId?: string; grossCents: number; commissionBps?: number; currency?: string; notes: string }
  ): Promise<{ entry: SettlementEntry }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-entries/manual-adjustment`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setSuperadminVenuePaymentRouting(
    venueId: string,
    paymentRouting: 'connect' | 'platform'
  ): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/payment-routing`, {
      method: 'PUT',
      body: JSON.stringify({ paymentRouting }),
    });
  }

  async setSuperadminVenueMercuryRecipient(
    venueId: string,
    mercuryRecipientId: string | null
  ): Promise<{ venue: Venue }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/mercury-recipient`, {
      method: 'PUT',
      body: JSON.stringify({ mercuryRecipientId }),
    });
  }

  // ---- Settlement statements (monthly rollups) ----

  async listSettlementStatements(venueId: string): Promise<{ statements: SettlementStatement[] }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-statements`);
  }

  // Venue-facing (non-superadmin) statement history: finalized + paid only.
  async getVenueSettlementStatements(venueId: string): Promise<{ statements: SettlementStatement[] }> {
    return this.request(`/api/admin/venues/${venueId}/settlement-statements`);
  }

  async generateSettlementStatement(
    venueId: string,
    periodStart: string
  ): Promise<{ statement: SettlementStatement; note: string | null }> {
    return this.request(`/api/admin/superadmin/venues/${venueId}/settlement-statements`, {
      method: 'POST',
      body: JSON.stringify({ periodStart }),
    });
  }

  async finalizeSettlementStatement(statementId: string): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/finalize`, { method: 'POST' });
  }

  async markSettlementStatementPaid(
    statementId: string,
    data: { paymentReference: string; notes?: string }
  ): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async paySettlementStatementViaMercury(
    statementId: string,
    data?: { notes?: string }
  ): Promise<{ statement: SettlementStatement }> {
    return this.request(`/api/admin/superadmin/settlement-statements/${statementId}/pay-mercury`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }
}

export interface SettlementEntry {
  id: string;
  venue_id: string;
  payment_id: string;
  booking_id: string | null;
  entry_type: 'charge' | 'refund' | 'manual_adjustment';
  gross_cents: number;
  commission_bps: number;
  commission_cents: number;
  net_to_venue_cents: number;
  currency: string;
  settlement_status: 'pending' | 'settled' | 'voided';
  settled_at: string | null;
  settlement_reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SettlementStatement {
  id: string;
  venue_id: string;
  period_start: string;
  period_end: string;
  entry_count: number;
  gross_cents: number;
  commission_cents: number;
  net_to_venue_cents: number;
  status: 'draft' | 'finalized' | 'paid' | 'voided';
  payment_method: 'check' | 'mercury_ach' | string;
  payment_reference: string | null;
  finalized_at: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const api = new ApiClient();
